import React, { ReactElement } from 'react'

import { RptFilterEntityInfo } from '../../Common/Types/Types'
import LocationalFiltering from './LocationalFiltering'
import MiscellaneousFiltering from './MiscellaneousFiltering'
import ScopeFiltering from './ScopeFiltering'
import { ReportTypes } from '../../../NonReact/constants/ReportTypes'
import DeviceFiltering from './DeviceTypeFiltering'
import { DEFAULT_ENTITY_TYPE_ID } from '../ReportTab/Constants'
import { SelectRangeItem } from '../../Common/Components/Selects'

// CSS imports. Commented out because of current build process.
// import './filterTab.scss'

/**
 * Component properties type.
 */
type Props = {
    filterEntityInfo: RptFilterEntityInfo | undefined
    telSys: number
    handleTelSysChangeByUser: (newValue: number) => void
    partitions: string[]
    setPartitions: React.Dispatch<React.SetStateAction<string[]>>
    telephoneNos: string
    setTelephoneNos: (newValue: string) => void
    telephoneNoDescriptions: string
    setTelephoneNoDescriptions: (newValue: string) => void
    devices: string
    setDevices: (newValue: string) => void
    ACDAgents: string
    setACDAgents: (newValue: string) => void
    filterLocationToFrom: number
    setFilterLocationToFrom: (newValue: number) => void
    channelTypes: number[]
    setChannelTypes: React.Dispatch<React.SetStateAction<number[]>>
    callTypeDirection: number
    setCallTypeDirection: (newValue: number) => void
    answerType: number
    setAnswerType: (newValue: number) => void
    callerID: number
    setCallerID: (newValue: number) => void
    DDIDigits: string
    setDDIDigits: (newValue: string) => void
    accountCodes: string
    setAccountCodes: (newValue: string) => void
    callIdentifier: string
    setCallIdentifier: (newValue: string) => void
    subjectFilter: string
    setSubjectFilter: (newValue: string) => void
    treatConfCallAsExternal: boolean
    setTreatConfCallAsExternal: (checked: boolean) => void
    supportsSubjectFiltering: boolean
    isReportTemplateUser: boolean
    deviceTypes: number[]
    setDeviceTypes: (newValue: number[]) => void
    currentTelSysType?: number
    reportType?: ReportTypes
    entityType: number
    availableDevices: SelectRangeItem[]
    setAvailableDevices: (devices: SelectRangeItem[]) => void
    selectedPartitionsPartIDOnTelSys: Nullable<number[]>
}

/**
 * Filter tab of report configuration modal window.
 *
 * @param filterEntityInfo The list of entities according to the filter.
 * @param telSys Report property.
 * @param handleTelSysChangeByUser Enhanced handler for telephone system change.
 * @param partitions Report property.
 * @param setPartitions Setter for corresponding report property.
 * @param telephoneNos Report property.
 * @param setTelephoneNos Setter for corresponding report property.
 * @param telephoneNoDescriptions Report property.
 * @param setTelephoneNoDescriptions Setter for corresponding report property.
 * @param devices Report property.
 * @param setDevices Setter for corresponding report property.
 * @param ACDAgents Report property.
 * @param setACDAgents Setter for corresponding report property.
 * @param filterLocationToFrom Report property.
 * @param setFilterLocationToFrom Setter for corresponding report property.
 * @param channelTypes Report property.
 * @param setChannelTypes Setter for corresponding report property.
 * @param callTypeDirection Report property.
 * @param setCallTypeDirection Setter for corresponding report property.
 * @param answerType Report property.
 * @param setAnswerType Setter for corresponding report property.
 * @param callerID Report property.
 * @param setCallerID Setter for corresponding report property.
 * @param DDIDigits Report property.
 * @param setDDIDigits Setter for corresponding report property.
 * @param accountCodes Report property.
 * @param setAccountCodes Setter for corresponding report property.
 * @param callIdentifier Report property.
 * @param setCallIdentifier Setter for corresponding report property.
 * @param subjectFilter Report property.
 * @param setSubjectFilter Setter for corresponding report property.
 * @param treatConfCallAsExternal Report property.
 * @param setTreatConfCallAsExternal Setter for corresponding report property.
 * @param supportsSubjectFiltering Whether the currently selected report style supports subject filtering.
 * @param isReportTemplateUser Whether the currently logged in user is a report template user.
 * @param deviceType device type filter.
 * @param setDeviceType Setter for device type filter.
 * @param currentTelSysType The current telephone system type.
 * @param reportType The current report type.
 * @param entityType The entity type for the report.
 * @param availableDevices The available devices for filtering.
 * @param setAvailableDevices Setter for available devices for filtering.
 * @param entityType The entity type for the report.
 * @param availableDevices The available devices for filtering.
 * @param setAvailableDevices Setter for available devices for filtering.
 * @param entityType The entity type for the report.
 * @param availableDevices The available devices for filtering.
 * @param setAvailableDevices Setter for available devices for filtering.
 * @param selectedPartitionsPartIDOnTelSys Selected partitions Part IDs on telephone system.
 *
 * @returns Filter tab component.
 *
 */
const FilterTab: React.FC<Props> = ({
    filterEntityInfo,
    telSys,
    handleTelSysChangeByUser,
    partitions,
    setPartitions,
    telephoneNos,
    setTelephoneNos,
    telephoneNoDescriptions,
    setTelephoneNoDescriptions,
    devices,
    setDevices,
    ACDAgents,
    setACDAgents,
    filterLocationToFrom,
    setFilterLocationToFrom,
    channelTypes,
    setChannelTypes,
    callTypeDirection,
    setCallTypeDirection,
    answerType,
    setAnswerType,
    callerID,
    setCallerID,
    DDIDigits,
    setDDIDigits,
    accountCodes,
    setAccountCodes,
    callIdentifier,
    setCallIdentifier,
    subjectFilter,
    setSubjectFilter,
    treatConfCallAsExternal,
    setTreatConfCallAsExternal,
    supportsSubjectFiltering,
    isReportTemplateUser,
    currentTelSysType,
    reportType,
    deviceTypes,
    setDeviceTypes,
    entityType,
    availableDevices,
    setAvailableDevices,
    selectedPartitionsPartIDOnTelSys
}: Props): ReactElement => {
    const isExtensionDeviceReport: boolean = reportType === ReportTypes.EXTENSION_LIST
    const isEntityTypeExensionReport: boolean = entityType === DEFAULT_ENTITY_TYPE_ID

    return (
        <div className='filter-tab tab-area'>
            {!isReportTemplateUser ? (
                <ScopeFiltering
                    telSys={telSys}
                    handleTelSysChangeByUser={handleTelSysChangeByUser}
                    partitions={partitions}
                    setPartitions={setPartitions}
                    filterEntityInfo={filterEntityInfo}
                />
            ) : (
                <></>
            )}
            {!isReportTemplateUser ? (
                <>
                    {isExtensionDeviceReport || isEntityTypeExensionReport ? (
                        <DeviceFiltering deviceTypes={deviceTypes} setDeviceTypes={setDeviceTypes} />
                    ) : (
                        <></>
                    )}
                    <LocationalFiltering
                        filterEntityInfo={filterEntityInfo}
                        telephoneNos={telephoneNos}
                        setTelephoneNos={setTelephoneNos}
                        telephoneNoDescriptions={telephoneNoDescriptions}
                        setTelephoneNoDescriptions={setTelephoneNoDescriptions}
                        devices={devices}
                        setDevices={setDevices}
                        ACDAgents={ACDAgents}
                        setACDAgents={setACDAgents}
                        filterLocationToFrom={filterLocationToFrom}
                        setFilterLocationToFrom={setFilterLocationToFrom}
                        telSys={telSys}
                        partitions={partitions}
                        reportType={reportType}
                        currentTelSysType={currentTelSysType}
                        selectedPartitionsPartIDOnTelSys={selectedPartitionsPartIDOnTelSys}
                        deviceTypes={deviceTypes}
                        availableDevices={availableDevices}
                        setAvailableDevices={setAvailableDevices}
                    />
                </>
            ) : (
                <></>
            )}

            <MiscellaneousFiltering
                filterEntityInfo={filterEntityInfo}
                channelTypes={channelTypes}
                setChannelTypes={setChannelTypes}
                callTypeDirection={callTypeDirection}
                setCallTypeDirection={setCallTypeDirection}
                answerType={answerType}
                setAnswerType={setAnswerType}
                callerID={callerID}
                setCallerID={setCallerID}
                DDIDigits={DDIDigits}
                setDDIDigits={setDDIDigits}
                accountCodes={accountCodes}
                setAccountCodes={setAccountCodes}
                callIdentifier={callIdentifier}
                setCallIdentifier={setCallIdentifier}
                subjectFilter={subjectFilter}
                setSubjectFilter={setSubjectFilter}
                treatConfCallAsExternal={treatConfCallAsExternal}
                setTreatConfCallAsExternal={setTreatConfCallAsExternal}
                supportsSubjectFiltering={supportsSubjectFiltering}
                isReportTemplateUser={isReportTemplateUser}
                currentTelSysType={currentTelSysType}
            />
        </div>
    )
}

export default FilterTab
